import en from '@/src/locales/en.json';
import tr from '@/src/locales/tr.json';

/**
 * The app's own UI strings — the handful of words this app owns that no
 * operator authors and no block ships: a breadcrumb label, an out-of-stock
 * badge, a "page 2 of 7".
 *
 * <p>They live in JSON catalogues, one per language, and are selected by the
 * ACTIVE locale's language subtag. Nothing in the code decides which language
 * to show: adding a language is adding a file here and registering it in
 * `CATALOGUES`, and the tenant's own locale set — from the site descriptor —
 * is what picks between them.
 *
 * <p>The fallback chain is: the active locale's language, then the SITE's
 * default locale's language, then whichever catalogue is registered first. The
 * middle step matters — a tenant serving a locale this app has no catalogue for
 * should fall back to that tenant's own default language, not to an arbitrary
 * one chosen at build time.
 */
export type Messages = Record<string, string>;

const CATALOGUES: Record<string, Messages> = { en, tr };

const language = (tag: string): string => {
  const dash = tag.indexOf('-');
  return (dash > 0 ? tag.slice(0, dash) : tag).toLowerCase();
};

export function getMessages(locale: string, siteDefaultLocale?: string): Messages {
  return (
    CATALOGUES[language(locale ?? '')] ??
    (siteDefaultLocale ? CATALOGUES[language(siteDefaultLocale)] : undefined) ??
    Object.values(CATALOGUES)[0]
  );
}

/** Look one string up, substituting `{name}` placeholders. Returns the key when
 *  a string is missing, so a gap is visible rather than silently blank. */
export function message(
  messages: Messages,
  key: string,
  params: Record<string, string | number> = {},
): string {
  const template = messages[key];
  if (template == null) return key;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

/**
 * The strings under one prefix, for a client component. Handing it the whole catalogue would
 * serialise every string on the page into the HTML; handing it the module would bundle every
 * language.
 */
export function messagesWithPrefix(messages: Messages, prefix: string): Messages {
  return Object.fromEntries(Object.entries(messages).filter(([key]) => key.startsWith(prefix)));
}
